import { Card, Flex, Space, Spin, Typography, notification } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeCardNumberElement } from "@stripe/stripe-js";
import { formatAddress } from "src/utils/format";
import { getLoginData } from "src/store/selectors/features/auth";
import { getLoggedInUserData } from "src/store/selectors/features/loggedInUser";
import RequestAppAction from "src/store/slices/appActions";
import PageHeader from "src/components/header/PageHeader";
import PaymentMethodForm from "src/components/payment-method";
import "../plan.scss";

const { Text } = Typography;

const PaymentMethod: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const REACT_APP_STRIPE_PUBLISHABLE_API_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_API_KEY ?? "";
  const maxPaymentIntentRetries = 1;

  const selectedUser = useSelector(getLoginData);
  const currentUser: any = useSelector(getLoggedInUserData);

  const [isLoading, setIsLoading] = useState(true);
  const [paymentIntentSecret, setPaymentIntentSecret] = useState<string>("");
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false);
  const [paymentIntentRetryCount, setPaymentIntentRetryCount] = useState(0);
  const [companyData, setCompanyData] = useState<any>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>();
  const [stripePromise, setStripePromise] = useState(() => 
    loadStripe(REACT_APP_STRIPE_PUBLISHABLE_API_KEY)
  )

  const onPaymentIntent = useCallback(() => {
    setIsCreatingPaymentIntent(true);

    dispatch(RequestAppAction.setUpStripePaymentMethod({
      data: {},
      cbSuccess: (res: any) => {
        setPaymentIntentSecret(res.client_secret);
        setIsCreatingPaymentIntent(false);
      },
      cbFailure: (e: string) => {
        notification.error({ message: e });
        setIsCreatingPaymentIntent(false);
      },
    }));
  }, [dispatch]);

  useEffect(() => {
    if (currentUser?.companies?.category && typeof currentUser.companies.category === "string") {
      const category = currentUser.companies.category.toLowerCase().trim();

      if (category === "unite") {
        navigate("/plan");
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (!companyData && selectedUser?.company) {
      const billingEmailAddress = selectedUser.company.billingEmailAddress || selectedUser.email || "";

      setCompanyData({
        ...selectedUser.company,
        billingEmailAddress,
      });
    }
  }, [selectedUser, companyData]);

  useEffect(() => {
    if (selectedUser && paymentIntentSecret) {
      setIsLoading(false);
    }
  }, [selectedUser, paymentIntentSecret]);

  useEffect(() => {
    if (
      selectedUser
      && !paymentIntentSecret
      && paymentIntentRetryCount <= maxPaymentIntentRetries
    ) {
      timeoutRef.current = setTimeout(() => {
        if (!paymentIntentSecret) {
          notification.error({
            message: t("notification.paymentIntentLoadFail"),
            description: t("notification.tryRefreshOrContactSupport"),
          });

          onPaymentIntent();
          setPaymentIntentRetryCount((prev: number) => prev + 1);
        }
      }, 5000);

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }
  }, [selectedUser, paymentIntentSecret, paymentIntentRetryCount, onPaymentIntent]);

  const setUpStripePaymentMethod = async (
    billingDetails: any,
    stripe: Stripe,
    cardElement: StripeCardNumberElement
  ) => {
    if (
      !companyData ||
      !stripe ||
      !cardElement ||
      !paymentIntentSecret ||
      !billingDetails
    ) {
      notification.error({ message: t("notification.createStripePaymentMethodFail") });
      return;
    }

    try {
      const result = await stripe.confirmCardSetup(
        paymentIntentSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          }
        }
      );

      if (result.error) {
        notification.error({ message: result.error.message || t("notification.createStripePaymentMethodFail") });
      } else {
        dispatch(RequestAppAction.createStripePaymentMethod({
          id: companyData.id,
          paymentMethodId: result.setupIntent.payment_method,
          cbSuccess: () => {
            notification.success({ message: t("paymentMethod.success") });
            navigate("/plan");
          },
          cbFailure: (e: string) => {
            // console.log(e);
            notification.error({ message: e });
            navigate("/plan");
          },
        }));
      }
    } catch (e) {
      // console.log(e);
      notification.error({ message: t("notification.createStripePaymentMethodFail") });
    }
  };

  const compareAddressData = (oldAddress: any, newAddress: any) => {
    if (!oldAddress || !newAddress) {
      return false;
    }

    if (
      oldAddress.country === newAddress.country &&
      oldAddress.state === newAddress.state &&
      oldAddress.city === newAddress.city &&
      oldAddress.postalCode === newAddress.postalCode
    ) {
      return true;
    }

    return false;
  };

  const onFinish = async (
    values: any,
    stripe: Stripe | null,
    cardElement: StripeCardNumberElement | null
  ) => {
    if (!values) {
      notification.error({ message: t("notification.paymentFormDataFail") });
      return;
    }

    if (!stripe || !cardElement) {
      notification.error({ message: t("paymentMethod.error") });
      return;
    }

    if (!paymentIntentSecret) {
      notification.error({ message: t("paymentMethod.error") });
      onPaymentIntent();
      return;
    }

    const country = values.country?.value
      ? (typeof values.country.value === "string" ? values.country.value : values.country)
      : selectedCountry;
    const province = values.province ? typeof values.province === "string" ? values.province : values.province.value : "";
    const state = values.state ? typeof values.state === "string" ? values.state : values.state.value : "";

    const address = formatAddress(
      values.postalCode,
      country,
      province,
      state,
      values.city
    );

    if (
      !address?.country || !address.state || !address.city || !address.postalCode
    ) {
      notification.error({ message: t("paymentMethod.addressRequired") });
      return;
    }

    if (!companyData) {
      notification.error({ message: t("paymentMethod.error") });
      return;
    }

    const paymentMethodBillingDetails = {
      address: {
        country: address.country,
        state: address.state,
        city: address.city,
        line1: address.line1,
        line2: address.line2,
        postal_code: address.postalCode,
      },
      email: companyData.billingEmailAddress,
      name: companyData.name,
    };

    const isAddressSame = compareAddressData(
      {
        country: companyData.country,
        state: companyData.state,
        city: companyData.city,
        postalCode: companyData.postalCode,
      },
      address
    );

    if (isAddressSame) {
      await setUpStripePaymentMethod(
        paymentMethodBillingDetails,
        stripe,
        cardElement
      );
    } else {
      // Update the address in the company data
      try {
        dispatch(RequestAppAction.updateCompany({
          data: {
            id: companyData.id,
            ...address
          },
          cbSuccess: (_res: any) => {
            setUpStripePaymentMethod(
              paymentMethodBillingDetails,
              stripe,
              cardElement
            )
          },
          cbFailure: (e: string) => {
            notification.error({ message: e });
          },
        }));
      } catch (e) {
        // console.log(e);
        notification.error({ message: t("notification.updateCompanyInfoFail") });
      }
    }
  };

  return (
    <div className="plan-page payment-method-page">
      <PageHeader pageTitle={t("paymentMethod.paymentMethod")} />
      <Spin spinning={isLoading || isCreatingPaymentIntent}>
        <Card className="plan-card">
          <Flex
            vertical={true}
            justify="center"
            align="center"
            gap={30}
            className="plan-card-container"
          >
            <Space
              direction="vertical"
              className={"plan-card-header"}
            >
              <Text className="plan-card-top">
                {t("paymentMethod.aboutPaymentMethod")}
              </Text>
              <Text className="plan-card-title">
                {t("paymentMethod.paymentMethod")}
              </Text>
              <Text className="plan-card-subtitle">
                {t("paymentMethod.subHeading")}
              </Text>
            </Space>
            <Elements stripe={stripePromise}>
              <PaymentMethodForm
                companyData={companyData}
                onPaymentIntent={onPaymentIntent}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                disableSubmit={!paymentIntentSecret}
                onFinish={onFinish}
              />
            </Elements>
          </Flex>
        </Card>
      </Spin>
    </div>
  );
};

export default PaymentMethod;
